import {
  SpeedDial as MuiSpeedDial,
  SpeedDialAction as MuiSpeedDialAction,
  styled,
} from '@mui/material';
import { SpeedDialFabType } from '../types';

/**
 * Speed Dial styling constants from Figma design
 */
const SPEED_DIAL_CONSTANTS = {
  /** Main FAB size */
  MAIN_SIZE: 56,
  /** Small FAB size */
  SMALL_SIZE: 24,
  /** Action button size */
  ACTION_SIZE: 36,
  /** Small action button size */
  ACTION_SIZE_SMALL: 24,
  /** Gap between actions */
  ACTIONS_GAP: 8,
  /** Default shadow */
  SHADOW: '0px 0px 16px 0px rgba(0, 0, 0, 0.08)',
  /** Small shadow */
  SHADOW_SMALL: '0px 0px 7px 0px rgba(0, 0, 0, 0.08)',
} as const;

interface StyledSpeedDialProps {
  fabType?: SpeedDialFabType;
}

/**
 * Styled SpeedDial with nova-ui theme
 */
export const StyledSpeedDial = styled(MuiSpeedDial, {
  shouldForwardProp: (prop) => prop !== 'fabType',
})<StyledSpeedDialProps>(({ theme, fabType = 'main' }) => {
  const isSmall = fabType === 'small';
  const size = isSmall
    ? SPEED_DIAL_CONSTANTS.SMALL_SIZE
    : SPEED_DIAL_CONSTANTS.MAIN_SIZE;

  return {
    // Actions container styling
    '& .MuiSpeedDial-actions': {
      gap: `${SPEED_DIAL_CONSTANTS.ACTIONS_GAP}px`,
      padding: 0,
      marginBottom: `${SPEED_DIAL_CONSTANTS.ACTIONS_GAP}px`,
      marginTop: `${SPEED_DIAL_CONSTANTS.ACTIONS_GAP}px`,
    },

    // Main FAB styling (direct child only, not action FABs)
    '& > .MuiFab-root': {
      width: size,
      height: size,
      minHeight: size,
      backgroundColor: theme.palette.primary.main,
      boxShadow: isSmall
        ? SPEED_DIAL_CONSTANTS.SHADOW_SMALL
        : SPEED_DIAL_CONSTANTS.SHADOW,
      transition: theme.transitions.create(
        ['background-color', 'box-shadow', 'transform'],
        { duration: theme.transitions.duration.short },
      ),

      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },

      // Open state - darker overlay
      '&.MuiSpeedDial-fabOpen': {
        background: `linear-gradient(90deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.3) 100%), ${theme.palette.primary.main}`,
      },

      '& svg': {
        width: isSmall ? 10 : 24,
        height: isSmall ? 10 : 24,
        color: theme.palette.common.white,
      },
    },
  };
});

/**
 * Styled SpeedDialAction with nova-ui theme
 */
export const StyledSpeedDialAction = styled(MuiSpeedDialAction)(
  ({ theme }) => ({
    '&&.MuiSpeedDialAction-fab': {
      width: SPEED_DIAL_CONSTANTS.ACTION_SIZE,
      height: SPEED_DIAL_CONSTANTS.ACTION_SIZE,
      minHeight: SPEED_DIAL_CONSTANTS.ACTION_SIZE,
      margin: 0,
      borderRadius: 30,
      backgroundColor: theme.palette.background.contrast,
      border: `1px solid ${theme.palette.text.fifth}`,
      boxShadow: SPEED_DIAL_CONSTANTS.SHADOW,
      transition: theme.transitions.create(
        ['background-color', 'border-color', 'box-shadow'],
        { duration: theme.transitions.duration.short },
      ),

      '&:hover': {
        backgroundColor: theme.palette.background.button,
        borderColor: 'transparent',
        boxShadow: SPEED_DIAL_CONSTANTS.SHADOW,
      },

      '& svg': {
        width: 16,
        height: 16,
        color: theme.palette.text.primary,
        transition: theme.transitions.create(['color'], {
          duration: theme.transitions.duration.short,
        }),
      },
    },
  }),
);
